/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect, useMemo, createContext, useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    useMultiStyleConfig,
    Box,
    Flex,
    IconButton,
    Button,
    useBreakpointValue,
    HStack,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    AuthHelpers,
    useAuthHelper,
    useCustomerType,
    usePage,
    useCustomer,
    useCustomerId
} from '@salesforce/commerce-sdk-react'

import {useCurrentBasket} from '../../hooks/use-current-basket'
import {useWishList} from '@app/hooks/use-wish-list'

import Link from '@app/components/link'
import Search from '../search'
import withRegistration from '../with-registration'
import {
    BrandLogo,
    BrandLogoWhite,
    HamburgerIcon,
    WishlistIcon,
    WishlistFilledIcon,
    SearchIcon
} from '../custom-icons'

import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import LoadingSpinner from '@app/components/loading-spinner'
import {
    isHydrated,
    noop,
    isCurrentPageFromPD,
    getEnvStaleTime,
    togglePageScroll,
    getPageDesignerRegion
} from '../../utils/utils'
import {HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {useLocation} from 'react-router-dom'
import {PageContext, Component} from '@salesforce/commerce-sdk-react/components'
import {PDCHeaderNavLinks} from '../../page-designer/layouts'
import {PDCLink, PDCHeaderTopBanner} from '../../page-designer/assets'
import useUpdateShopperContext from '@app/hooks/use-update-shopper-context'

// Constants
import {HOME_HREF} from '@app/constants'
import {
    HEADER_STYLE_VARIANTS,
    HEADER_TRANSPARENT_LIGHT_VARIANT,
    HEADER_TRANSPARENT_VARIANTS_PREFIX,
    HEADER_TRANSPARENT_SCROLLED_VARIANT,
    HEADER_PDC_PAGE_ID,
    HEADER_OUTLET_PDC_PAGE_ID
} from './constants'
import useMultiSite from '../../hooks/use-multi-site'
import {usePageParameters} from '@app/hooks/use-page-parameters'

const PAGEDESIGNER_TO_COMPONENT = {
    'commerce_assets.pdcLink': PDCLink,
    'commerce_assets.pdcHeaderTopBanner': PDCHeaderTopBanner,
    'commerce_layouts.pdcHeaderNavLinks': PDCHeaderNavLinks
}

const IconButtonWithRegistration = withRegistration(IconButton)
const LinkWithRegistration = withRegistration(Link)

export const HeaderNavLinksContext = createContext()

/**
 * The header is the main source for accessing
 * navigation, search, basket, and other
 * important information and actions. It persists
 * on the top of your application and will
 * respond to changes in device size.
 *
 * To customize the styles, update the themes
 * in theme/components/project/header.js
 * @param  props
 * @param   {func} props.onMenuClick click event handler for menu button
 * @param   {object} props.searchInputRef reference of the search input
 * @param   {func} props.onMyAccountClick click event handler for my account button
 * @return  {React.ReactElement} - Header component
 */
const Header = ({
    children,
    onMenuClick = noop,
    onMyAccountClick = noop,
    onWishlistClick = noop,
    onStoreLocatorClick = noop,
    drawerMenuTriggerRef,
    isRegularHeader = true,
    hideNavigationControls = false,
    ...props
}) => {
    const intl = useIntl()
    const {site, isOutletSite} = useMultiSite()
    const {
        derivedData: {totalItems, bonusProductLineItemsCount}
    } = useCurrentBasket()
    const {
        derivedData: {onlineCustomerProductListItems}
    } = useWishList()
    const wishlistLength = onlineCustomerProductListItems?.length || 0
    const customerId = useCustomerId()
    const {isRegistered} = useCustomerType()

    const customer = useCustomer(
        {parameters: {customerId}},
        {enabled: !!customerId && isRegistered}
    )
    const logout = useAuthHelper(AuthHelpers.Logout)
    const navigate = useNavigation()
    const isDesktop = useBreakpointValue(
        {base: false, lg: true},
        {
            fallback: undefined
        }
    )
    const isNotDesktop = useBreakpointValue(
        {base: true, lg: false},
        {
            fallback: undefined
        }
    )
    const location = useLocation()

    const [showLoading, setShowLoading] = useState(false)
    const [showSearch, setShowSearch] = useState(false)
    const [isFirstShowSearch, setIsFirstShowSearch] = useState(false)

    /**
     * Generates a unique identifier for the homepage based on the current site's ID.
     *
     * This function retrieves the site's ID, converts it to a lowercase string,
     * and appends the suffix '-homepage' to create a unique identifier for the homepage.
     *
     * @returns {string} A string representing the unique homepage identifier in the format '{siteId}-homepage'.
     */
    const getHomePageId = () => {
        const siteId = `${site?.id}`.toLowerCase()
        return `${siteId}-homepage`
    }

    // if current page is a homepage or page designer page, get its header style attribute
    // and apply it as a variant
    const pageId = location.pathname?.endsWith(HOME_HREF)
        ? getHomePageId()
        : isCurrentPageFromPD(location)
        ? location.pathname.split('/').pop()
        : ''
    const staleTime = getEnvStaleTime()
    const {data: currentPage} = usePage(
        {parameters: {pageId}},
        {enabled: pageId != null && pageId.length > 0 ? true : false, staleTime: staleTime}
    )
    const headerStyle = currentPage?.data?.headerStyle
    const headerStyleVariant = HEADER_STYLE_VARIANTS[headerStyle] || ''
    const [variant, setVariant] = useState(headerStyleVariant)
    const {updateShopperContextAfterLogout} = useUpdateShopperContext()

    // function which change the header variant when scrolling
    const changeVariantWhenScroll = useMemo(() => {
        return () => {
            if (isHydrated()) {
                setVariant(
                    window.scrollY > 0 ? HEADER_TRANSPARENT_SCROLLED_VARIANT : headerStyleVariant
                )
            }
        }
    }, [headerStyleVariant])

    useEffect(() => {
        // we have to apply the variant each time the location changes
        setVariant(headerStyleVariant)

        const addScrollListener = () => {
            if (isHydrated()) {
                window.addEventListener('scroll', changeVariantWhenScroll)
            }
        }

        const removeScrollListener = () => {
            if (isHydrated()) {
                window.removeEventListener('scroll', changeVariantWhenScroll)
            }
        }

        if (headerStyleVariant.startsWith(HEADER_TRANSPARENT_VARIANTS_PREFIX)) {
            // add event listener to change the header style when scrolling
            // if the header style is transparent
            addScrollListener()
        } else {
            // otherwise remove the event listener
            removeScrollListener()
        }

        return () => {
            removeScrollListener()
        }
    }, [headerStyleVariant, changeVariantWhenScroll])
    const parameters = usePageParameters(
        isOutletSite ? HEADER_OUTLET_PDC_PAGE_ID : HEADER_PDC_PAGE_ID,
        {includeCountry: true}
    )

    // Get the header page from page designer to display the navigation links
    const {data: headerPage} = usePage(
        {parameters},
        {staleTime},
        {enabled: Boolean(parameters?.c_country)}
    )
    const headerNavLinksComponent = getPageDesignerRegion(headerPage, 'main')?.components?.find(
        (comp) => comp?.typeId === 'commerce_layouts.pdcHeaderNavLinks'
    )
    const [contextValue] = useState({components: PAGEDESIGNER_TO_COMPONENT})

    const styles = useMultiStyleConfig('Header', {variant})

    const onSignoutClick = async () => {
        setShowLoading(true)
        const logoutResult = await logout.mutateAsync()
        await updateShopperContextAfterLogout()
        navigate('/login')
        setShowLoading(false)
    }

    const toggleSearchPanel = () => {
        setShowSearch((prevState) => !prevState)
    }

    const searchIconRef = useRef(null)
    const searchIcon = (
        <IconButton
            aria-label={intl.formatMessage({
                id: 'header.button.assistive_msg.search',
                defaultMessage: 'Search'
            })}
            aria-expanded={showSearch}
            icon={<SearchIcon boxSize={['14px', null, '12px']} />}
            variant="unstyled"
            {...styles.icons}
            onClick={toggleSearchPanel}
            ref={searchIconRef}
        ></IconButton>
    )

    useEffect(() => {
        // mark the first time the search panel is shown to allow toogle focus on search button
        if (!isFirstShowSearch && showSearch) {
            setIsFirstShowSearch(true)
        }

        if (!showSearch) {
            if (isFirstShowSearch) {
                searchIconRef.current?.focus()
            }
            togglePageScroll(!showSearch)
        }
    }, [showSearch])

    return (
        <Box as="header" {...styles.container} {...props} id="header">
            <Box {...styles.content}>
                {showLoading && (
                    <LoadingSpinner isBodyLocked={true} wrapperStyles={{height: '100dvh'}} />
                )}
                <Flex
                    wrap="wrap"
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    height={isRegularHeader ? '' : '50px'}
                >
                    <HStack spacing={6}>
                        {/** Burger icon */}
                        {isRegularHeader && !hideNavigationControls && (
                            <IconButton
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.menu',
                                    defaultMessage: 'Menu'
                                })}
                                icon={<HamburgerIcon boxSize="22px" />}
                                variant="unstyled"
                                {...styles.icons}
                                onClick={onMenuClick}
                                ref={drawerMenuTriggerRef}
                            />
                        )}

                        {/** Search icon (mobile) */}
                        {isHydrated() &&
                            isNotDesktop &&
                            isRegularHeader &&
                            !hideNavigationControls &&
                            searchIcon}
                        {/** Navigation links from page designer */}
                        {headerNavLinksComponent && isRegularHeader && (
                            <HideOnMobile>
                                <PageContext.Provider value={contextValue}>
                                    <HeaderNavLinksContext.Provider value={variant}>
                                        <Component component={headerNavLinksComponent} />
                                    </HeaderNavLinksContext.Provider>
                                </PageContext.Provider>
                            </HideOnMobile>
                        )}
                        <Box {...styles.bodyContainer}>{children}</Box>
                    </HStack>
                    {/** Logo */}
                    <IconButton
                        as={Link}
                        to={HOME_HREF}
                        aria-label={intl.formatMessage({
                            defaultMessage: 'Vilebrequin Homepage',
                            id: 'checkout_header.back'
                        })}
                        icon={
                            variant === HEADER_TRANSPARENT_LIGHT_VARIANT ? (
                                <BrandLogoWhite {...styles.logo} />
                            ) : (
                                <BrandLogo {...styles.logo} />
                            )
                        }
                        {...styles.icons}
                        display={'flex'}
                        position="absolute"
                        mx="auto"
                        left="50%"
                        transform="translateX(-50%)"
                    />
                    {isRegularHeader && (
                        <HStack as="ul" listStyleType="none" spacing={[1, null, 2]}>
                            {/** Search icon (desktop) */}
                            {isDesktop && !hideNavigationControls && <li>{searchIcon}</li>}
                            {/** Wishlist */}
                            <li>
                                {isHydrated() &&
                                wishlistLength !== undefined &&
                                wishlistLength !== null ? (
                                    isDesktop ? (
                                        <LinkWithRegistration
                                            to="/account/wishlist"
                                            {...styles.links}
                                            onClick={onWishlistClick}
                                        >
                                            {intl.formatMessage({
                                                defaultMessage: 'WISHLIST',
                                                id: 'header.button.wishlist'
                                            })}
                                            {wishlistLength > 0 && ` (${wishlistLength})`}
                                        </LinkWithRegistration>
                                    ) : (
                                        isNotDesktop && (
                                            <IconButtonWithRegistration
                                                aria-label={intl.formatMessage({
                                                    defaultMessage: 'Favourites',
                                                    id: 'header.button.assistive_msg.wishlist'
                                                })}
                                                icon={
                                                    wishlistLength > 0 ? (
                                                        <WishlistFilledIcon
                                                            boxSize="14px"
                                                            svgTitle={intl.formatMessage({
                                                                defaultMessage: 'Favourites',
                                                                id: 'header.button.assistive_msg.wishlist'
                                                            })}
                                                            svgDescription={intl.formatMessage({
                                                                defaultMessage: 'Favourites',
                                                                id: 'header.button.assistive_msg.wishlist'
                                                            })}
                                                        />
                                                    ) : (
                                                        <WishlistIcon
                                                            boxSize="14px"
                                                            svgTitle={intl.formatMessage({
                                                                defaultMessage: 'Favourites',
                                                                id: 'header.button.assistive_msg.wishlist'
                                                            })}
                                                            svgDescription={intl.formatMessage({
                                                                defaultMessage: 'Favourites',
                                                                id: 'header.button.assistive_msg.wishlist'
                                                            })}
                                                        />
                                                    )
                                                }
                                                variant="unstyled"
                                                {...styles.icons}
                                                {...styles.wishlistIcon}
                                                onClick={onWishlistClick}
                                            />
                                        )
                                    )
                                ) : (
                                    <Skeleton height={15} width={15} m={12} />
                                )}
                            </li>

                            {/** Account Menu */}
                            {isDesktop && isRegistered && isHydrated() && (
                                <>
                                    <li>
                                        <Link
                                            to="/account"
                                            {...styles.links}
                                            onClick={onMyAccountClick}
                                        >
                                            {intl.formatMessage(
                                                {
                                                    id: 'header.button.hi_account',
                                                    defaultMessage: 'Hi {name}'
                                                },
                                                {
                                                    name: customer.data?.firstName || ''
                                                }
                                            )}
                                        </Link>
                                    </li>
                                    <li>
                                        <Button
                                            variant="link"
                                            {...styles.links}
                                            onClick={onSignoutClick}
                                        >
                                            {intl.formatMessage({
                                                defaultMessage: 'Log out',
                                                id: 'header.popover.action.log_out'
                                            })}
                                        </Button>
                                    </li>
                                </>
                            )}
                            {isDesktop && !(isRegistered && isHydrated()) && (
                                <li>
                                    <LinkWithRegistration
                                        to="/account"
                                        {...styles.links}
                                        onClick={onMyAccountClick}
                                    >
                                        {intl.formatMessage({
                                            id: 'header.button.my_account',
                                            defaultMessage: 'ACCOUNT'
                                        })}
                                    </LinkWithRegistration>
                                </li>
                            )}

                            {/** Cart */}
                            <li>
                                <Link to="/cart" {...styles.links}>
                                    {intl.formatMessage(
                                        {
                                            id: 'header.button.my_cart_with_num_items',
                                            defaultMessage: 'CART ({numItems})'
                                        },
                                        {numItems: totalItems + bonusProductLineItemsCount}
                                    )}
                                </Link>
                            </li>
                        </HStack>
                    )}
                </Flex>
            </Box>
            {showSearch && isRegularHeader && (
                <Box {...styles.searchContainer}>
                    <Search onClose={toggleSearchPanel} {...styles.search} />
                </Box>
            )}
        </Box>
    )
}

Header.propTypes = {
    children: PropTypes.node,
    onMenuClick: PropTypes.func,
    onMyAccountClick: PropTypes.func,
    onWishlistClick: PropTypes.func,
    onStoreLocatorClick: PropTypes.func,
    searchInputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.elementType})
    ]),
    drawerMenuTriggerRef: PropTypes.object,
    isRegularHeader: PropTypes.bool,
    hideNavigationControls: PropTypes.bool
}

export default Header
